import { DateTime, Duration } from 'luxon';

const dateUtils = {
  getTimezoneOffsetMinutes(ianaTimezone: string) {
    // console.log('getTimezoneOffsetMinutes', { ianaTimezone, offset: DateTime.now().setZone(ianaTimezone).offset });
    return DateTime.now().setZone(ianaTimezone).offset;
  },
  compare(a: string, b: string) {
    return DateTime.fromISO(a).toJSDate() - DateTime.fromISO(b).toJSDate();
  },
  getVersion(): any {
    return '1.0 (using luxon)';
  },

  now() {
    return DateTime.now();
  },

  isPast(to: string) {
    let isPast = false;
    if (to != null && DateTime.fromISO(to) < DateTime.now()) {
      isPast = true;
    }
    // console.log('isPast', { to, isPast });
    return isPast;
  },

  isBetween(now: any, from: string, to: string) {
    // console.log('isBetween', { now: now.toUTC().toISO(), from, to });
    if (from != null || to != null) {
      if (from != null && to != null && DateTime.fromISO(from) <= now && now <= DateTime.fromISO(to)) {
        return true;
      }
      if (from == null && to != null && now <= DateTime.fromISO(to)) {
        return true;
      }
      if (from != null && to == null && DateTime.fromISO(from) <= now) {
        return true;
      }
    }
    return false;
  },

  getTimezoneAbbreviation(ianaTimezone: string) {
    // console.log('getTimezoneAbbreviation', { ianaTimezone });
    return DateTime.local().setZone(ianaTimezone).toFormat('ZZZZ');
  },

  /**
   *
   * @param isoDateString Returns a nicely formatted date in the given timezone
   * @param ianaTimezone
   * @param formatString
   * @returns
   */
  format(isoDateString: string | null | undefined, ianaTimezone: string, formatString: string = 'MM/dd/yyyy hh:mm a') {
    // console.log('format', { ianaTimezone, isoDateString });
    if (isoDateString == null) {
      return null;
    }
    return DateTime.fromISO(isoDateString, { zone: ianaTimezone }).toFormat(formatString);
  },

  formatFromUTC(isoDateString: string | null | undefined, ianaTimezone: string, formatString: string = 'MM/dd/yyyy hh:mm a') {
    const newDateStr = `${isoDateString.slice(0, 23)}Z`;
    return this.format(newDateStr, ianaTimezone, formatString);
  },

  getTimestamp(ianaTimezone: string) {
    return DateTime.utc().toISO();
  },

  /**
   * Gets a luxon date object as if user were in the given timezone (useful for controls that bind with a date object)
   */
  toDate(isoDateString: string | null | undefined, ianaTimezone: string) {
    // console.log('toLocalDate', { isoDateString, ianaTimezone });
    if (!isoDateString) {
      return null;
    }
    const a = DateTime.fromISO(isoDateString, { zone: ianaTimezone });
    const b = a.setZone(DateTime.local().zoneName, { keepLocalTime: true });
    return b;
  },

  /**
   * Gets a javascript date object from an ISO string. eg.
   */
  toJSDate(isoDateString: string | null | undefined, ianaTimezone: string) {
    // console.log('toLocalDate', { isoDateString, ianaTimezone });
    if (!isoDateString) {
      return null;
    }
    const a = DateTime.fromISO(isoDateString, { zone: ianaTimezone });
    const b = a.setZone(DateTime.local().zoneName, { keepLocalTime: true });
    return b.toJSDate();
  },

  toFuelCalendarDate(isoDateString: string | null | undefined, ianaTimezone: string) {
    // console.log('toLocalDate', { isoDateString, ianaTimezone });
    if (!isoDateString) {
      return null;
    }
    const a = DateTime.fromISO(isoDateString, { zone: ianaTimezone });
    const b = a.setZone(DateTime.local().zoneName, { keepLocalTime: true });
    return b.toJSDate();
  },

  /**
   * Takes a date object and returns the ISO representation
   */
  toString(localDate: Date | DateTime | null | undefined, ianaTimezone: string) {
    // console.log('toISO', { localDate, ianaTimezone });
    if (!localDate) {
      return null;
    }
    return DateTime.fromJSDate(localDate).setZone(ianaTimezone, { keepLocalTime: true }).toUTC().toISO();
  },

  isUTC(input: string) {
    const dt = DateTime.fromISO(input, { setZone: true });
    return dt.offset === 0;
  },
  convertDateStrLocalToTimeZoneSpecificDate(isoDateString: string | null | undefined, ianaTimezone: string) {
    // console.log('toLocalDate', { isoDateString, ianaTimezone });
    if (!isoDateString) {
      return null;
    }
    const a = DateTime.fromISO(isoDateString, { zone: ianaTimezone });
    return a.toISO();
  },

  convertDateLocalToTimeZoneSpecificJSDate(date: Date | null | string, ianaTimezone: string, keepLocalTime = true) {
    // console.log('toLocalDate', { date, ianaTimezone });
    if (!date) {
      return null;
    }
    const a = DateTime.fromJSDate(date).setZone(ianaTimezone, { keepLocalTime });
    return a.toISO();
  },
  isDateTimeObject(date) {
    return date instanceof DateTime;
  },
  isDateString(variable) {
    // Basic check if the variable is a string
    if (typeof variable !== 'string') return false;

    // Attempt to parse the date string
    const date = DateTime.fromISO(variable);

    // Check if the parsed date is valid
    return date.isValid;
  },
  getDifferenceFromDates(date1, date2) {
    const start = dateUtils.isDateString(date1) ? DateTime.fromISO(date1) : DateTime.fromJSDate(date1);
    const end = dateUtils.isDateString(date2) ? DateTime.fromISO(date2) : DateTime.fromJSDate(date2);
    const dif = start.diff(end, ['years', 'months', 'days', 'hours', 'minutes', 'seconds']);
    return dif;
  },
  getDifferenceDurationByUnit(date1, date2, unit = 'seconds') {
    const start = dateUtils.isDateString(date1) ? DateTime.fromISO(date1) : DateTime.fromJSDate(date1);
    const end = dateUtils.isDateString(date2) ? DateTime.fromISO(date2) : DateTime.fromJSDate(date2);
    const diff = start.diff(end, [unit]);

    return diff[unit];
  },
  // Duration.fromObject({seconds:59.7}).toFormat('hh:mm:ss') // => '00:00:60'
  formatDuration(duration, format = 'hh:mm:ss', unit = 'seconds') {
    // console.log('formatDuration', { duration, format, unit });
    if (typeof duration === 'string' && duration.indexOf(':') > -1) {
      return duration;
    }
    const toReturn = Duration.fromObject({ [unit]: duration }).toFormat(format);
    return toReturn;
  },
  getDefaultDateRangeUTC() {
    const to = DateTime.utc();
    const from = to.minus({ hours: 1 });

    return {
      dateFrom: from.toISO(),
      dateTo: to.toISO(),
    };
  },
};

export { dateUtils };
